/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/ColumnHeaderMenu.tsx
 * FILE_ID: QB-SCOPESHEET-COLMENU-001
 * PURPOSE: Inline column-header dropdown so estimators can hide the active
 *          column or toggle any optional/advanced column without leaving
 *          the grid (no Setup tab round-trip — Excel parity).
 * WHY: User feedback: "I want the ease and flexibility like Excel, not
 *      having to click so many things to build a quote." Setup → Visible
 *      columns is too far. Header click is one click.
 * FLOW:
 *   ┌────────────┐    ┌────────────────┐    ┌─────────────────────────┐
 *   │ Click ▾ on │ -> │ Popover lists  │ -> │ onUpdateConfig({        │
 *   │ any header │    │ all toggleable │    │   visible_columns: ids  │
 *   │            │    │ optional cols  │    │ }) → grid re-renders    │
 *   └────────────┘    └────────────────┘    └─────────────────────────┘
 * DEPENDENCIES:
 *   - BASE_COLUMN_BANDS — declares which columns are `optional`
 *   - ScopeSheetGridProps.onUpdateConfig — persists the toggle
 */

import { useEffect, useRef, useState } from 'react'
import { Eye, EyeOff, ChevronDown, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Column } from './ScopeSheetGridTypes'
import { BASE_COLUMN_BANDS } from './scope-sheet-columns'

interface ColumnHeaderMenuProps {
  /** The column whose header was clicked. Used for the contextual "Hide this column" affordance. */
  column: Column
  /** Currently visible optional column ids (mirror of `quote.config.visible_columns`). */
  visibleColumnIds: string[]
  /** Persist the toggle. Caller is expected to merge `{ visible_columns }` into `quote.config`. */
  onUpdateConfig: (patch: Record<string, unknown>) => void
  /** Optional: jump straight to the Custom Columns / Setup tab for renaming, defaults, etc. */
  onOpenSetup?: () => void
}

interface ToggleableColumn {
  id: string
  label: string
  type: 'text' | 'number' | 'currency'
  computed: boolean
  bandLabel: string
}

function readToggleableColumns(): ToggleableColumn[] {
  // Pull every `optional: true` column declared in BASE_COLUMN_BANDS. Today
  // that's the "advanced" band, but anything tagged optional anywhere will
  // surface here automatically.
  const out: ToggleableColumn[] = []
  for (const band of BASE_COLUMN_BANDS) {
    for (const col of band.columns) {
      if (col.optional) {
        out.push({
          id: col.id,
          label: col.label,
          type: (col.type as ToggleableColumn['type']) ?? 'text',
          computed: !!col.computed,
          bandLabel: band.label,
        })
      }
    }
  }
  return out
}

const PRESETS: { id: string; label: string; columnIds: string[] }[] = [
  {
    id: 'cost-tracking',
    label: 'Cost tracking',
    columnIds: ['labour_cost', 'material_cost', 'plant_cost', 'subcon_cost', 'total_cost', 'margin'],
  },
  {
    id: 'plant-detail',
    label: 'Plant detail',
    columnIds: ['plant_description', 'plant_unit_cost', 'plant_quantity', 'plant_cost'],
  },
  {
    id: 'subcon-detail',
    label: 'Subcon detail',
    columnIds: ['subcon_description', 'subcon_quote', 'subcon_days', 'subcon_cost'],
  },
  {
    id: 'notes',
    label: 'Notes',
    columnIds: ['internal_notes', 'client_visible_notes'],
  },
]

export default function ColumnHeaderMenu({
  column,
  visibleColumnIds,
  onUpdateConfig,
  onOpenSetup,
}: ColumnHeaderMenuProps) {
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  // Close on outside click + Esc — standard popover dismissal so the popover
  // never blocks normal grid interaction.
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const toggleableColumns = readToggleableColumns()
  const visibleSet = new Set(visibleColumnIds)
  const isThisColumnOptional = !!column.optional
  const isThisColumnVisible = visibleSet.has(column.id)

  const persist = (next: Set<string>) => {
    onUpdateConfig({ visible_columns: Array.from(next) })
  }

  const toggleId = (id: string) => {
    const next = new Set(visibleSet)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    persist(next)
  }

  const togglePreset = (ids: string[]) => {
    const next = new Set(visibleSet)
    const allOn = ids.every((id) => next.has(id))
    for (const id of ids) {
      if (allOn) next.delete(id)
      else next.add(id)
    }
    persist(next)
  }

  const showAll = () => persist(new Set(toggleableColumns.map((c) => c.id)))
  const hideAll = () => persist(new Set())

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((o) => !o)
        }}
        title="Column options (hide / show optional columns)"
        className={cn(
          'flex-shrink-0 ml-1 w-3 h-3 flex items-center justify-center rounded',
          'opacity-40 hover:opacity-100 hover:bg-slate-200 dark:hover:bg-slate-700',
          'transition-opacity',
          open && 'opacity-100 bg-slate-200 dark:bg-slate-700',
        )}
      >
        <ChevronDown className="w-2.5 h-2.5" />
      </button>

      {open && (
        <div
          ref={popoverRef}
          className="absolute top-full left-0 mt-0.5 z-30 w-72 max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl text-slate-700 dark:text-slate-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header — current-column action */}
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">
              {column.label}
            </div>
            {isThisColumnOptional ? (
              <button
                onClick={() => {
                  toggleId(column.id)
                  setOpen(false)
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                {isThisColumnVisible ? (
                  <>
                    <EyeOff className="w-3.5 h-3.5 text-slate-500" />
                    Hide this column
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5 text-emerald-500" />
                    Show this column
                  </>
                )}
              </button>
            ) : (
              <div className="text-[11px] text-slate-400 italic px-2 py-1">
                Mandatory — can&apos;t be hidden
              </div>
            )}
          </div>

          {/* Quick presets */}
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
              Quick presets
            </div>
            <div className="flex flex-wrap gap-1">
              {PRESETS.map((preset) => {
                const allOn = preset.columnIds.every((id) => visibleSet.has(id))
                const someOn =
                  !allOn && preset.columnIds.some((id) => visibleSet.has(id))
                return (
                  <button
                    key={preset.id}
                    onClick={() => togglePreset(preset.columnIds)}
                    className={cn(
                      'px-2 py-0.5 rounded text-[10px] font-medium border transition-colors',
                      allOn
                        ? 'bg-rose-600 text-white border-rose-600'
                        : someOn
                        ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border-rose-300 dark:border-rose-700'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700',
                    )}
                  >
                    {preset.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Optional columns checklist */}
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                Optional columns ({visibleSet.size}/{toggleableColumns.length})
              </div>
              <div className="flex gap-1">
                <button
                  onClick={showAll}
                  className="text-[10px] text-rose-600 dark:text-rose-300 hover:underline"
                >
                  Show all
                </button>
                <span className="text-[10px] text-slate-300 dark:text-slate-600">·</span>
                <button
                  onClick={hideAll}
                  className="text-[10px] text-slate-500 hover:underline"
                >
                  Hide all
                </button>
              </div>
            </div>
            <div className="space-y-0.5 max-h-48 overflow-y-auto">
              {toggleableColumns.map((c) => {
                const on = visibleSet.has(c.id)
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleId(c.id)}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-[11px] hover:bg-slate-100 dark:hover:bg-slate-800',
                      on && 'bg-rose-50 dark:bg-rose-900/20 text-rose-900 dark:text-rose-100',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {on ? (
                        <Eye className="w-3 h-3 flex-shrink-0 text-rose-500 dark:text-rose-300" />
                      ) : (
                        <EyeOff className="w-3 h-3 flex-shrink-0 text-slate-400" />
                      )}
                      <span className="truncate">{c.label}</span>
                    </div>
                    <span
                      className={cn(
                        'flex-shrink-0 px-1 py-px rounded text-[8px] font-bold uppercase tracking-wider',
                        c.computed
                          ? 'bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200'
                          : c.type === 'currency'
                          ? 'bg-emerald-200 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200'
                          : c.type === 'number'
                          ? 'bg-cyan-200 dark:bg-cyan-900/50 text-cyan-800 dark:text-cyan-200'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200',
                      )}
                    >
                      {c.computed ? 'calc' : c.type}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Setup link — for adding net-new custom columns */}
          {onOpenSetup && (
            <button
              onClick={() => {
                onOpenSetup()
                setOpen(false)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <Plus className="w-3 h-3" />
              Add a custom column…
            </button>
          )}
        </div>
      )}
    </>
  )
}
